import type { GoalTypes } from '@interfaces/goalEnums';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Typography } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useGoalCreationMethodChoice = () => {
  type MethodChoiceResult = {
    choice: typeof selectedMethod;
    closeDialog: () => void;
  };
  const [resolver, setResolver] = useState<
    ((result: MethodChoiceResult) => void) | null
  >(null);
  const hasPending = !!resolver;

  /** Modal state */
  const [selectedMethod, setSelectedMethod] = useState<
    { method: 'EXISTING_GOAL'; goal: Goal } | { method: 'NEW_GOAL' } | null
  >(null);
  const [goalType, setGoalType] = useState<GoalTypes | null>(null);
  const [activeGoals, setActiveGoals] = useState<Goal[]>([]);
  const [allowNewGoal, setAllowNewGoal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(!selectedMethod);
  const [disableCancelButton, setDisableCancelButton] =
    useState<boolean>(false);
  /** ************* */

  const { t } = useTranslation(['client', 'workflowCompletions']);
  const goalTypeDisplay = t(`goalType.${goalType}`);

  useEffect(() => {
    setLoading(!selectedMethod);
  }, [selectedMethod]);

  const closeDialog = () => {
    setDisableCancelButton(false);
    setResolver(null);
    setSelectedMethod(null);
    setGoalType(null);
    setAllowNewGoal(false);
  };

  const getGoalCreationMethodChoice = async (args: {
    goalType: GoalTypes;
    activeGoals: Goal[];
    allowNewGoal: boolean;
  }) =>
    new Promise<MethodChoiceResult>((resolve) => {
      setGoalType(args.goalType);
      setActiveGoals(args.activeGoals);
      setResolver(() => resolve);
      setAllowNewGoal(args.allowNewGoal);
    });

  return {
    getGoalCreationMethodChoice,
    goalCreationMethodChoiceDialog: (
      <ConfirmationModal
        title={t(
          'workflowCompletions:createGoal.goalCreationMethodChoice.title',
        )}
        confirmButtonLabel={t(
          'workflowCompletions:createGoal.goalCreationMethodChoice.confirmButtonLabel',
        )}
        open={hasPending}
        confirmButtonColor='primary'
        onConfirm={() => {
          setDisableCancelButton(true);
          resolver?.({
            choice: selectedMethod,
            closeDialog,
          });
        }}
        onCancel={() => {
          resolver?.({
            choice: null,
            closeDialog,
          });
        }}
        loading={loading}
        disableCancelButton={disableCancelButton}
      >
        <Typography variant='bodyLarge'>
          {allowNewGoal
            ? t(
                'workflowCompletions:createGoal.goalCreationMethodChoice.body',
                { numGoals: activeGoals?.length, goalType: goalTypeDisplay },
              )
            : t(
                'workflowCompletions:createGoal.goalCreationMethodChoice.bodyNoNewGoal',
                { numGoals: activeGoals?.length, goalType: goalTypeDisplay },
              )}
        </Typography>
        <SelectionTile
          sx={{ mt: 2 }}
          type='radio'
          options={[
            ...activeGoals.map((goal) => ({
              label: t(
                'workflowCompletions:createGoal.goalCreationMethodChoice.existingGoal',
                {
                  goalName: goal.name || goalTypeDisplay,
                  marketValue: formatMoneyValue(
                    goal.statistics?.marketValueCents || 0,
                  ),
                },
              ),
              value: goal.id,
            })),
            ...(allowNewGoal
              ? [
                  {
                    label: t(
                      'workflowCompletions:createGoal.goalCreationMethodChoice.newGoal',
                      { goalType: goalTypeDisplay },
                    ),
                    value: 'NEW_GOAL',
                  },
                ]
              : []),
          ]}
          value={
            selectedMethod?.method === 'NEW_GOAL'
              ? 'NEW_GOAL'
              : (selectedMethod?.goal?.id as string)
          }
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            if (e.target.value === 'NEW_GOAL') {
              setSelectedMethod({ method: 'NEW_GOAL' });
            } else {
              setSelectedMethod({
                method: 'EXISTING_GOAL',
                goal: activeGoals.find(
                  (goal) => goal.id === e.target.value,
                ) as Goal,
              });
            }
          }}
        />
      </ConfirmationModal>
    ),
  };
};
